'use client';

import React, { Fragment, useEffect, useRef } from 'react';
import type { ReactNode, FunctionComponent, SyntheticEvent } from 'react';
import classNames from 'classnames';
import Scrollable from '@/components/ui/Scrollable';
import { getScrollParent } from '@/components/helpers/getScrollParent';
import { ActiveLink } from '@/components/ui/ActiveLink/ActiveLink';
import Icon, { IconsAvailable } from '@/components/ui/Icon';
import './styles.scss';

export interface ITabsItemProps {
  title: ReactNode;
  description?: ReactNode;
  descriptionIntent?: 'default' | 'danger' | 'warning' | 'success';
  active?: boolean;
  href?: string;
  exact?: boolean;
  onClick?: (e: SyntheticEvent) => void;
  disabled?: boolean;
  icon?: IconsAvailable;
}

export interface ITabsProps {
  items: ITabsItemProps[];
  theme?: 'default' | 'small' | 'well';
  layout?: 'default' | 'vertical' | 'grid';
  showArrows?: boolean;
  className?: string;
  stretch?: boolean;
  isRoutable?: boolean;
}

const RenderTabs: FunctionComponent<ITabsProps> = (props) => {
  const activeTabElement = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (!activeTabElement.current) {
      return;
    }

    const el = activeTabElement.current;
    const parent = getScrollParent(el) as HTMLDivElement | Window;

    if (!(parent instanceof Window)) {
      parent.scrollLeft = el.offsetLeft - parent.getBoundingClientRect().width / 2 + el.getBoundingClientRect().width / 2;
    }
  }, []);

  const TabContent: FunctionComponent<ITabsItemProps> = (r) => {
    return (
      <Fragment>
        {r.icon && (
          <Icon
            width={18}
            height={18}
            kind={r.icon}
          />
        )}

        <span className="tabs__list__wrapper">
          <span className="tabs__list__title">{r.title}</span>

          {r.description && (
            <span
              className={classNames('tabs__list__description', {
                'intent-default': r.descriptionIntent === 'default' || !r.descriptionIntent,
                'intent-danger': r.descriptionIntent === 'danger',
                'intent-warning': r.descriptionIntent === 'warning',
                'intent-success': r.descriptionIntent === 'success',
              })}
            >
              {r.description}
            </span>
          )}
        </span>
      </Fragment>
    );
  };

  return (
    <div
      className={classNames(
        'tabs',
        {
          'theme-default': props.theme === 'default' || !props.theme,
          'theme-small': props.theme === 'small',
          'theme-well': props.theme === 'well',

          'layout-default': !props.layout || props.layout === 'default',
          'layout-vertical': props.layout === 'vertical',
          'layout-grid': props.layout === 'grid',

          'stretch': props.stretch,
        },
        props.className,
      )}
    >
      <ul className="tabs__list">
        {props.items.map((r, ri) => (
          <li key={ri}>
            {props.isRoutable ? (
              <ActiveLink
                href={r.href || ''}
                exact={r.exact}
                activeRef={activeTabElement}
              >
                <TabContent {...r} />
              </ActiveLink>
            ) : (
              <a
                href={r.href}
                onClick={r.onClick}
                draggable={false}
                aria-current={r.active}
                ref={r.active ? activeTabElement : undefined}
                aria-disabled={r.disabled}
              >
                <TabContent {...r} />
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Tabs: FunctionComponent<ITabsProps> = (props) => {
  if (props.items.length < 2) {
    return null;
  }

  if (!props.layout || props.layout === 'default') {
    return (
      <Scrollable
        hideScrollbar
        hideShadows
        hideArrows={!props.showArrows}
        className={classNames('tabs__scroller', {
          'has-arrows': props.showArrows,
        })}
      >
        <RenderTabs {...props} />
      </Scrollable>
    );
  }

  return <RenderTabs {...props} />;
};

Tabs.displayName = 'Tabs';

export default Tabs;
